import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Fab
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import {
  FilterList as FilterListIcon
} from '@mui/icons-material';
import axios from 'axios';
import ItemCard from '../../components/market/ItemCard';
import ItemDetailsDialog from '../../components/market/ItemDetailsDialog';
import MarketHeader from '../../components/market/MarketHeader';
import InfoDialog from '../../components/market/InfoDialog';
import LeaderboardDialog from '../../components/market/LeaderboardDialog';

// Filter categories that map to multiple assetName values from /market/landing
const ASSET_TYPE_GROUPS = {
  TOTEMS: ['TOTEMS', 'TOTEM_FRAGMENTS', 'TOTEM_ITEMS'], // claims, fragments, totems
};

/**
 * MarketItems Component - Displays and manages Splinterlands items market
 */
const MarketItems = ({ setIsSidebar, isMobile }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // State management
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAssetType, setSelectedAssetType] = useState('ALL');
  const [sortBy, setSortBy] = useState('price_asc'); // Default: cheapest first
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  // Dialog states for MarketHeader
  const [showInfo, setShowInfo] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  // Pagination - rendering all ~900+ items at once (each with its own image request)
  // saturates the main thread and network for many seconds, making the whole page feel
  // unresponsive. Render in batches and grow via infinite scroll, same pattern as the
  // cards market page (src/scenes/market/index.jsx).
  const itemsPerPage = 60;
  const [visibleCount, setVisibleCount] = useState(itemsPerPage);
  const sentinelRef = useRef(null);

  // Load items from API
  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get('https://vapi.splinterlands.com/market/landing');
      
      if (response.data && response.data.status === 'success' && response.data.data && response.data.data.assets) {
        setItems(response.data.data.assets);
      } else {
        throw new Error('Invalid API response format');
      }
    } catch (err) {
      console.error('Error loading items:', err);
      setError('Failed to load items. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Listen for filter changes from sidebar
  useEffect(() => {
    const handleFilterChange = (event) => {
      const filters = event.detail;
      setSearchTerm(filters.searchTerm || '');
      setSelectedAssetType(filters.selectedAssetType || 'ALL');
      setSortBy(filters.sortBy || 'price_asc');
    };

    window.addEventListener('itemFiltersChanged', handleFilterChange);
    return () => window.removeEventListener('itemFiltersChanged', handleFilterChange);
  }, []);

  // Filter and search items
  useEffect(() => {
    let filtered = [...items];

    // Always exclude DEEDS (they have their own Land tab)
    filtered = filtered.filter(item => item.assetName !== 'DEEDS');

    // Filter by asset type (some filter categories span several API asset names)
    if (selectedAssetType !== 'ALL') {
      const assetNames = ASSET_TYPE_GROUPS[selectedAssetType] || [selectedAssetType];
      filtered = filtered.filter(item => assetNames.includes(item.assetName));
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.detailName?.toLowerCase().includes(searchLower) ||
        item.assetDescription?.toLowerCase().includes(searchLower) ||
        item.detailDescription?.toLowerCase().includes(searchLower)
      );
    }

    // Sort items
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':
          return (a.detailName || '').localeCompare(b.detailName || '');
        case 'name_desc':
          return (b.detailName || '').localeCompare(a.detailName || '');
        case 'price_asc': {
          const priceA = a.prices && a.prices.length > 0 ? a.prices[0].minPrice : Infinity;
          const priceB = b.prices && b.prices.length > 0 ? b.prices[0].minPrice : Infinity;
          return priceA - priceB;
        }
        case 'price_desc': {
          const priceA = a.prices && a.prices.length > 0 ? a.prices[0].minPrice : -1;
          const priceB = b.prices && b.prices.length > 0 ? b.prices[0].minPrice : -1;
          return priceB - priceA;
        }
        case 'listed_desc':
          return (b.numListed || 0) - (a.numListed || 0);
        case 'listed_asc':
          return (a.numListed || 0) - (b.numListed || 0);
        default:
          return 0;
      }
    });

    setFilteredItems(filtered);
  }, [items, selectedAssetType, searchTerm, sortBy]);

  // Handle item click
  const handleItemClick = (item) => {
    setSelectedItem(item);
    setDetailsDialogOpen(true);
  };

  // Handle buy click
  const handleBuyClick = (item) => {
    setSelectedItem(item);
    setDetailsDialogOpen(true);
  };

  // Close details dialog
  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedItem(null);
  };

  const visibleItems = filteredItems.slice(0, visibleCount);

  // Infinite scroll: grow visibleCount as the sentinel element scrolls into view
  useEffect(() => {
    if (!sentinelRef.current) return;
    if (visibleCount >= filteredItems.length) return;
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount(prev => prev + itemsPerPage);
        }
      },
      { root: null, rootMargin: '200px', threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [visibleCount, filteredItems.length]);

  return (
    <Box m="20px">
      {/* Market Header with Integrated Tabs */}
      <MarketHeader 
        title="Splinterlands Items Market"
        onInfoClick={() => setShowInfo(true)}
        onLeaderboardClick={() => setShowLeaderboard(true)}
      />
      
      {/* Dialogs (managed by this component) */}
      <InfoDialog open={showInfo} onClose={() => setShowInfo(false)} />
      <LeaderboardDialog open={showLeaderboard} onClose={() => setShowLeaderboard(false)} />

      {/* Results Summary */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ color: colors.grey[100] }}>
          {loading ? 'Loading items...' : `${filteredItems.length} items found`}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {selectedAssetType !== 'ALL' && (
            <Chip
              label={selectedAssetType}
              onDelete={() => setSelectedAssetType('ALL')}
              sx={{
                backgroundColor: colors.blueAccent[800],
                color: colors.grey[100]
              }}
            />
          )}
          {searchTerm && (
            <Chip
              label={`Search: ${searchTerm}`}
              onDelete={() => setSearchTerm('')}
              sx={{
                backgroundColor: colors.blueAccent[800],
                color: colors.grey[100]
              }}
            />
          )}
          {sortBy !== 'price_asc' && (
            <Chip
              label={`Sort: ${sortBy.replace('_', ' ')}`}
              onDelete={() => setSortBy('price_asc')}
              sx={{
                backgroundColor: colors.blueAccent[800],
                color: colors.grey[100]
              }}
            />
          )}
        </Box>
      </Box>

      {/* Content */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress sx={{ color: colors.blueAccent[500] }} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : (
        <>
          {/* Same grid as the cards market page (src/scenes/market/index.jsx) */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(auto-fill, minmax(140px, 1fr))',
                sm: 'repeat(auto-fill, minmax(180px, 1fr))',
                md: 'repeat(auto-fill, minmax(200px, 1fr))',
                lg: 'repeat(auto-fill, minmax(220px, 1fr))',
              },
              gap: { xs: 1, sm: 2 },
            }}
          >
            {visibleItems.map((item) => (
              <Box key={`${item.assetName}-${item.detailId}`}>
                <ItemCard
                  item={item}
                  onItemClick={handleItemClick}
                  onBuyClick={handleBuyClick}
                />
              </Box>
            ))}
          </Box>
          {/* Infinite Scroll Sentinel */}
          {visibleCount < filteredItems.length && (
            <div ref={sentinelRef} style={{ height: 1 }} />
          )}
        </>
      )}

      {/* No Results */}
      {!loading && !error && items.length > 0 && filteredItems.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          p={4}
        >
          <FilterListIcon sx={{ fontSize: 64, color: colors.grey[600], mb: 2 }} />
          <Typography variant="h6" sx={{ color: colors.grey[400], mb: 1 }}>
            No items found
          </Typography>
          <Typography variant="body2" sx={{ color: colors.grey[500] }}>
            Try adjusting your search or filters
          </Typography>
        </Box>
      )}

      {/* Item Details Dialog */}
      <ItemDetailsDialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        item={selectedItem}
      />
      
      {/* Mobile Filter Button */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="filter"
          onClick={() => setIsSidebar(true)}
          sx={{
            position: 'fixed',
            bottom: 20,
            left: 20,
            zIndex: 1000,
            backgroundColor: colors.blueAccent[600],
            '&:hover': {
              backgroundColor: colors.blueAccent[700]
            }
          }}
        >
          <FilterListIcon />
        </Fab>
      )}
    </Box>
  );
};

export default MarketItems;
